import { useEffect, useState } from 'react';
import { apiClient as defaultClient, type ApiClient } from '../api/client';
import { Spinner } from '../components/Spinner';
import styles from './ScanActivityIndicator.module.css';

const POLL_INTERVAL_MS = 4000;

export interface ScanActivityIndicatorProps {
  orgId: string;
  projectId: string;
  client?: ApiClient;
  /** Poll cadence override (tests). */
  pollIntervalMs?: number;
}

/**
 * A persistent, page-to-page "scan in progress" indicator. There is no tangible
 * crawl-progress signal to report — robots `Crawl-delay` pacing makes any
 * percentage dishonest — so this deliberately shows an INDETERMINATE spinner,
 * not a progress bar, while the viewed project has at least one initiated or
 * running scan. Hosted once in the persistent RootLayout so it survives
 * navigation and keeps polling as the user moves page to page.
 */
export function ScanActivityIndicator({
  orgId,
  projectId,
  client = defaultClient,
  pollIntervalMs = POLL_INTERVAL_MS,
}: ScanActivityIndicatorProps) {
  const [activeCount, setActiveCount] = useState(0);
  // On a scan's discovery detail route, THAT page is the authoritative polite
  // announcer — suppress this indicator's live region to avoid double SR output.
  // (Re-evaluated on each poll re-render; read from the live URL, no Router dep.)
  const onDiscovery = /\/scans\/[^/]+\/discovery$/.test(window.location.pathname);

  useEffect(() => {
    let cancelled = false;
    setActiveCount(0);

    async function poll() {
      try {
        const scans = await client.listScans(orgId, projectId);
        if (cancelled) return;
        const active = scans.filter(
          (s) => s.status === 'initiated' || s.status === 'running',
        ).length;
        setActiveCount(active);
      } catch {
        // Transient poll failures never nag the user; keep the last good state.
      }
    }

    void poll();
    const timer = window.setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [orgId, projectId, client, pollIntervalMs]);

  if (activeCount < 1) {
    return null;
  }

  const label = activeCount > 1 ? `${activeCount} scans in progress` : 'Scan in progress';

  return (
    <div
      className={styles.indicator}
      role={onDiscovery ? undefined : 'status'}
      aria-live={onDiscovery ? undefined : 'polite'}
    >
      <span className={styles.spin}>
        <Spinner />
      </span>
      <span className={styles.label}>{label}</span>
    </div>
  );
}
